import React from 'react';
import { Task } from '@/types';
import { CheckCircle, Circle, Clock } from 'lucide-react';

interface TaskCardProps {
  task: Task;
  onTaskComplete?: (taskId: string) => void;
  onTaskClick?: (taskId: string) => void;
}

const TaskCard: React.FC<TaskCardProps> = ({ task, onTaskComplete, onTaskClick }) => {
  const handleCardClick = () => {
    onTaskClick?.(task.id);
  };

  const handleCompleteClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onTaskComplete?.(task.id);
  };

  const getPriorityColor = (priority?: number) => {
    // Priority mapping: 0 = Low, 1 = Normal, 2 = High, 3 = Urgent
    switch (priority) {
      case 0:
        return 'text-green-500';
      case 1:
        return 'text-yellow-500';
      case 2:
        return 'text-orange-500';
      case 3:
        return 'text-red-500';
      default:
        return 'text-gray-500';
    }
  };

  const getPriorityLabel = (priority?: number) => {
    switch (priority) {
      case 0:
        return 'Low';
      case 1:
        return 'Normal';
      case 2:
        return 'High';
      case 3:
        return 'Urgent';
      default:
        return 'Unknown';
    }
  };

  return (
    <div 
      className="bg-background border border-gray-200 rounded-lg p-4 shadow-sm cursor-pointer hover:shadow-md transition-shadow"
      onClick={handleCardClick}
    >
      <div className="flex items-start justify-between">
        <div className="flex-1 mr-3">
          <div className="flex items-center mb-2">
            {task.priority !== undefined && task.priority !== null && (
              <Clock size={14} className={`mr-2 ${getPriorityColor(task.priority)}`} />
            )}
            <h3 className={`font-medium text-black ${task.status === 'completed' ? 'line-through text-gray-500' : ''}`}>
              {task.taskTitle}
            </h3>
          </div>
          {task.description && (
            <p className="text-sm text-gray-600 mb-1">{task.description}</p>
          )}
          <div className="flex items-center gap-4 text-xs text-gray-500">
            <span>Priority: {getPriorityLabel(task.priority)}</span>
            <span>Status: {task.status.replace('_', ' ')}</span>
          </div>
          <p className="text-sm text-gray-600 mt-1">
            Created: {new Date(task.createdAt).toLocaleDateString()}
          </p>
          {task.dueDate && (
            <p className="text-xs text-gray-400 mt-1">
              Due: {new Date(task.dueDate).toLocaleDateString()}
            </p>
          )}
        </div>
        <button
          onClick={handleCompleteClick}
          className="flex-shrink-0 p-1 hover:bg-gray-100 rounded transition-colors"
          disabled={task.status === 'completed' || task.status === 'cancelled'}
        >
          {task.status === 'completed' ? (
            <CheckCircle size={20} className="text-green-500" />
          ) : task.status === 'cancelled' ? (
            <Circle size={20} className="text-red-400" />
          ) : (
            <Circle size={20} className="text-gray-400" />
          )}
        </button>
      </div>
    </div>
  );
};

export default TaskCard; 